'use client';

import { useState, useEffect, type ReactNode, type CSSProperties } from 'react';
import type {
	TQrExperienceActionType,
	TQrExperiencePublicDto,
	TExperienceSocialPlatform,
	TOpeningHourItem,
	TQrExperienceTheme,
} from '@shared/schemas';
import { PLATFORMS_BY_ID } from './socialPlatforms';
import { FACILITIES_BY_ID } from './facilities';
import {
	Clock,
	ChevronDown,
	MapPin,
	Share2,
	Save,
	ExternalLink,
	Utensils,
	FileText,
	Download,
	Copy,
	Check,
	Phone,
	Mail,
	Globe,
	Smartphone,
	Sun,
	Moon,
	Sparkles,
	SlidersHorizontal,
	UserPlus,
	ArrowUpRight,
} from 'lucide-react';
import { ExchangeContactDialog } from './ExchangeContactDialog';

function recordAction(
	slug: string,
	actionType: TQrExperienceActionType,
	targetPlatform?: TExperienceSocialPlatform,
	targetLabel?: string,
) {
	void fetch(`${process.env.NEXT_PUBLIC_API_URL}/qr-experience/public/${slug}/actions`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({
			actionType,
			...(targetPlatform ? { targetPlatform } : {}),
			...(targetLabel ? { targetLabel } : {}),
		}),
		keepalive: true,
	}).catch(() => undefined);
}

function ActionLink({
	href,
	type,
	targetPlatform,
	targetLabel,
	slug,
	className,
	style,
	children,
	download,
}: {
	href: string;
	type: TQrExperienceActionType;
	targetPlatform?: TExperienceSocialPlatform;
	targetLabel?: string;
	slug: string;
	className?: string;
	style?: CSSProperties;
	children: ReactNode;
	download?: string;
}) {
	const isDataPdf = href.startsWith('data:application/pdf');
	const external =
		href.startsWith('http://') || href.startsWith('https://') || href.startsWith('/api/');

	const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
		recordAction(slug, type, targetPlatform, targetLabel);

		// Modern browsers block top-frame navigation to data: URLs.
		// If href is still a base64 data URL (e.g. in preview mode), convert to Blob URL to view or download.
		if (isDataPdf) {
			e.preventDefault();
			try {
				const base64Index = href.indexOf('base64,');
				const base64Data = base64Index !== -1 ? href.slice(base64Index + 7) : '';
				const byteCharacters = atob(base64Data);
				const byteNumbers = new Array(byteCharacters.length);
				for (let i = 0; i < byteCharacters.length; i++) {
					byteNumbers[i] = byteCharacters.charCodeAt(i);
				}
				const byteArray = new Uint8Array(byteNumbers);
				const blob = new Blob([byteArray], { type: 'application/pdf' });
				const blobUrl = URL.createObjectURL(blob);

				if (type === 'download-pdf') {
					const dlLink = document.createElement('a');
					dlLink.href = blobUrl;
					dlLink.download = download || `${targetLabel || 'document'}.pdf`;
					document.body.appendChild(dlLink);
					dlLink.click();
					document.body.removeChild(dlLink);
				} else {
					window.open(blobUrl, '_blank', 'noopener,noreferrer');
				}
				setTimeout(() => URL.revokeObjectURL(blobUrl), 30_000);
			} catch (err) {
				console.error('Error opening/downloading data PDF:', err);
			}
		}
	};

	return (
		<a
			href={isDataPdf ? '#' : href}
			target={external ? '_blank' : undefined}
			rel={external ? 'noopener noreferrer' : undefined}
			download={download}
			onClick={handleClick}
			className={className}
			style={style}
		>
			{children}
		</a>
	);
}

function saveContact(experience: TQrExperiencePublicDto) {
	if (experience.content.type !== 'business-card') return;
	const contact = experience.content;
	const vcard = [
		'BEGIN:VCARD',
		'VERSION:3.0',
		`FN:${contact.fullName}`,
		contact.company && `ORG:${contact.company}`,
		contact.role && `TITLE:${contact.role}`,
		contact.phone && `TEL:${contact.phone}`,
		contact.email && `EMAIL:${contact.email}`,
		contact.website && `URL:${contact.website}`,
		contact.address && `ADR:;;${contact.address}`,
		contact.bio && `NOTE:${contact.bio}`,
		'END:VCARD',
	]
		.filter(Boolean)
		.join('\r\n');
	const url = URL.createObjectURL(new Blob([vcard], { type: 'text/vcard;charset=utf-8' }));
	const link = document.createElement('a');
	link.href = url;
	link.download = `${contact.fullName.replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '') || 'contact'}.vcf`;
	link.click();
	window.setTimeout(() => URL.revokeObjectURL(url), 1_000);
}

const DEFAULT_HOURS: TOpeningHourItem[] = [
	{ day: 'Monday', open: '08:00 am', close: '07:00 pm', isClosed: false },
	{ day: 'Tuesday', open: '08:00 am', close: '07:00 pm', isClosed: false },
	{ day: 'Wednesday', open: '08:00 am', close: '07:00 pm', isClosed: false },
	{ day: 'Thursday', open: '08:00 am', close: '07:00 pm', isClosed: false },
	{ day: 'Friday', open: '08:00 am', close: '07:00 pm', isClosed: false },
	{ day: 'Saturday', open: '08:00 am', close: '07:00 pm', isClosed: false },
	{ day: 'Sunday', open: '08:00 am', close: '07:00 pm', isClosed: false },
];

function getTodayName(): string {
	return new Intl.DateTimeFormat('en-US', { weekday: 'long' }).format(new Date());
}

function parseTimeToMinutes(timeStr: string): number {
	const match = timeStr.trim().match(/^(\d{1,2}):(\d{2})\s*(am|pm)?$/i);
	if (!match || !match[1] || !match[2]) return 0;
	let hours = parseInt(match[1], 10);
	const minutes = parseInt(match[2], 10);
	const meridiem = match[3]?.toLowerCase();
	if (meridiem === 'pm' && hours < 12) hours += 12;
	if (meridiem === 'am' && hours === 12) hours = 0;
	return hours * 60 + minutes;
}

function getLiveOpeningStatus(hoursList: TOpeningHourItem[]) {
	const todayName = getTodayName();
	const todaySchedule = hoursList.find(
		(item) => item.day.toLowerCase() === todayName.toLowerCase(),
	);

	if (!todaySchedule || todaySchedule.isClosed) {
		return { isOpen: false, statusText: 'Closed today', detail: 'Opens next business day' };
	}

	const now = new Date();
	const currentMinutes = now.getHours() * 60 + now.getMinutes();
	const openMinutes = parseTimeToMinutes(todaySchedule.open);
	const closeMinutes = parseTimeToMinutes(todaySchedule.close);

	if (currentMinutes >= openMinutes && currentMinutes < closeMinutes) {
		return { isOpen: true, statusText: 'Open now', detail: `Closes ${todaySchedule.close}` };
	} else if (currentMinutes < openMinutes) {
		return { isOpen: false, statusText: 'Closed', detail: `Opens ${todaySchedule.open}` };
	} else {
		return { isOpen: false, statusText: 'Closed', detail: 'Opens tomorrow' };
	}
}

function hexToHsl(hex: string): { h: number; s: number; l: number } {
	let cleaned = hex.replace('#', '');
	if (cleaned.length === 3) {
		cleaned = cleaned
			.split('')
			.map((c) => c + c)
			.join('');
	}
	const num = parseInt(cleaned, 16);
	if (isNaN(num)) return { h: 224, s: 82, l: 48 };

	const r = ((num >> 16) & 255) / 255;
	const g = ((num >> 8) & 255) / 255;
	const b = (num & 255) / 255;

	const max = Math.max(r, g, b);
	const min = Math.min(r, g, b);
	let h = 0;
	let s = 0;
	const l = (max + min) / 2;

	if (max !== min) {
		const d = max - min;
		s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
		switch (max) {
			case r:
				h = (g - b) / d + (g < b ? 6 : 0);
				break;
			case g:
				h = (b - r) / d + 2;
				break;
			case b:
				h = (r - g) / d + 4;
				break;
		}
		h /= 6;
	}

	return {
		h: Math.round(h * 360),
		s: Math.round(s * 100),
		l: Math.round(l * 100),
	};
}

export function computeHeaderGradientStyle(hexColor?: string): {
	backgroundColor: string;
	backgroundImage: string;
	glowColor: string;
} {
	const hex = hexColor && /^#[0-9A-Fa-f]{6}$/.test(hexColor) ? hexColor : '#1d4ed8';
	const { h, s } = hexToHsl(hex);
	const sat = Math.max(s, 65);
	const primaryGlow = `hsla(${h}, ${sat}%, 55%, 0.85)`;
	const secondaryGlow = `hsla(${(h + 28) % 360}, ${sat}%, 52%, 0.65)`;
	const ambientDepth = `hsla(${(h + 15) % 360}, 45%, 16%, 0.95)`;
	const darkBase = `hsl(${h}, 35%, 6%)`;

	return {
		backgroundColor: darkBase,
		backgroundImage: `
			radial-gradient(at 18% 18%, ${primaryGlow} 0px, transparent 65%),
			radial-gradient(at 85% 80%, ${secondaryGlow} 0px, transparent 55%),
			radial-gradient(at 50% 50%, ${ambientDepth} 0px, transparent 100%)
		`,
		glowColor: `hsl(${h}, ${sat}%, 55%)`,
	};
}

export function getThemeTokens(theme: TQrExperienceTheme = 'glass') {
	switch (theme) {
		case 'dark':
			return {
				isDark: true,
				pageBg: 'bg-slate-950 text-slate-100',
				cardBg: 'bg-slate-900/95 border-slate-800 text-white shadow-xl',
				subCardBg: 'bg-slate-800/90 border-slate-700/80 text-slate-200',
				textPrimary: 'text-white',
				textSecondary: 'text-slate-400',
				textMuted: 'text-slate-500',
				borderMuted: 'border-slate-800',
				actionBg: 'bg-slate-800 hover:bg-slate-700 border-slate-700 text-white',
				badgeBg: 'bg-slate-800 text-slate-200 border-slate-700',
			};
		case 'light':
			return {
				isDark: false,
				pageBg: 'bg-slate-50 text-slate-900',
				cardBg: 'bg-white border-slate-100 text-slate-900 shadow-sm',
				subCardBg: 'bg-slate-50 border-slate-100 text-slate-700',
				textPrimary: 'text-slate-900',
				textSecondary: 'text-slate-500',
				textMuted: 'text-slate-400',
				borderMuted: 'border-slate-100',
				actionBg: 'bg-white hover:bg-slate-50 border-slate-200/90 text-slate-900',
				badgeBg: 'bg-slate-100 text-slate-700 border-slate-200',
			};
		case 'minimal':
			return {
				isDark: false,
				pageBg: 'bg-white text-black',
				cardBg: 'bg-white border-2 border-black text-black shadow-none rounded-none',
				subCardBg: 'bg-slate-50 border border-black text-black rounded-none',
				textPrimary: 'text-black',
				textSecondary: 'text-slate-700',
				textMuted: 'text-slate-500',
				borderMuted: 'border-black',
				actionBg:
					'bg-white hover:bg-slate-100 border-2 border-black text-black rounded-none font-bold',
				badgeBg: 'bg-black text-white rounded-none',
			};
		case 'glass':
		default:
			return {
				isDark: true,
				pageBg: 'bg-gradient-to-b from-slate-950 via-slate-900 to-zinc-950 text-white',
				cardBg: 'backdrop-blur-xl bg-white/10 border-white/20 text-white shadow-2xl',
				subCardBg: 'backdrop-blur-md bg-white/5 border-white/10 text-slate-200',
				textPrimary: 'text-white',
				textSecondary: 'text-slate-300',
				textMuted: 'text-slate-400',
				borderMuted: 'border-white/15',
				actionBg: 'backdrop-blur-md bg-white/10 hover:bg-white/20 border-white/20 text-white',
				badgeBg: 'backdrop-blur-md bg-white/15 text-white border-white/20',
			};
	}
}

function ThemeToggleWidget({
	theme,
	onChange,
}: {
	theme: TQrExperienceTheme;
	onChange: (theme: TQrExperienceTheme) => void;
}) {
	const cycle = () => {
		const order: Record<TQrExperienceTheme, TQrExperienceTheme> = {
			glass: 'dark',
			dark: 'light',
			light: 'minimal',
			minimal: 'glass',
		};
		onChange(order[theme] || 'glass');
	};

	return (
		<button
			type="button"
			onClick={cycle}
			className="fixed sm:absolute top-3.5 right-3.5 z-40 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-900/80 hover:bg-slate-950 backdrop-blur-md text-white border border-white/20 shadow-md text-xs font-semibold transition-all hover:scale-105 active:scale-95 cursor-pointer"
			title={`Current theme: ${theme}. Click to switch theme.`}
		>
			{theme === 'glass' && <Sparkles className="size-3.5 text-purple-300" />}
			{theme === 'dark' && <Moon className="size-3.5 text-amber-300" />}
			{theme === 'light' && <Sun className="size-3.5 text-amber-400" />}
			{theme === 'minimal' && <SlidersHorizontal className="size-3.5 text-slate-200" />}
			<span className="capitalize text-[11px] font-medium">{theme}</span>
		</button>
	);
}

function PublicExperienceFooter({ isDark }: { isDark: boolean }) {
	return (
		<footer className="w-full pt-8 pb-4 text-center mt-6">
			<a
				href="/create"
				target="_blank"
				rel="noopener noreferrer"
				className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-medium border transition-all hover:scale-105 active:scale-95 shadow-xs ${
					isDark
						? 'bg-slate-900/80 hover:bg-slate-800 text-slate-300 hover:text-white border-white/15 backdrop-blur-md'
						: 'bg-white/90 hover:bg-white text-slate-600 hover:text-slate-900 border-slate-200/90 backdrop-blur-md'
				}`}
			>
				<span className="font-semibold text-emerald-500">Ekutu QR</span>
				<span className="opacity-40">•</span>
				<span>Create your QR code for free</span>
				<ArrowUpRight className="size-3.5 opacity-70" />
			</a>
		</footer>
	);
}

export function PublicExperiencePage({
	experience,
	previewMode = false,
}: {
	experience: TQrExperiencePublicDto;
	previewMode?: boolean;
}) {
	const content = experience.content;
	const contentTheme = (content as { theme?: TQrExperienceTheme }).theme || 'glass';
	const [activeTheme, setActiveTheme] = useState<TQrExperienceTheme>(contentTheme);

	useEffect(() => {
		if ((content as { theme?: TQrExperienceTheme }).theme) {
			setActiveTheme((content as { theme?: TQrExperienceTheme }).theme!);
		}
	}, [(content as { theme?: TQrExperienceTheme }).theme]);

	const themeTokens = getThemeTokens(activeTheme);
	const [hoursOpen, setHoursOpen] = useState(false);
	const [phoneCopied, setPhoneCopied] = useState(false);
	const [couponCopied, setCouponCopied] = useState(false);
	const [showExchangeModal, setShowExchangeModal] = useState(false);

	const copyPhoneNumber = (phoneNum: string, e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();
		void navigator.clipboard.writeText(phoneNum);
		setPhoneCopied(true);
		window.setTimeout(() => setPhoneCopied(false), 2000);
	};

	// 1. Social Links Page
	if (content.type === 'social-links') {
		const accentColor = content.accentColor || '#1d4ed8';
		const headerColor = content.headerColor || accentColor;
		const headerGradient = computeHeaderGradientStyle(headerColor);

		if (previewMode) {
			return (
				<div
					className={`w-full relative pb-10 transition-colors duration-300 ${themeTokens.pageBg}`}
				>
					<ThemeToggleWidget theme={activeTheme} onChange={setActiveTheme} />
					{/* Aurora Hero Banner */}
					<div
						className="relative h-32 flex flex-col items-center justify-start pt-6 text-white overflow-hidden"
						style={{
							backgroundColor: headerGradient.backgroundColor,
							backgroundImage: headerGradient.backgroundImage,
						}}
					>
						<div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff0d_1px,transparent_1px),linear-gradient(to_bottom,#ffffff0d_1px,transparent_1px)] bg-[size:14px_14px] pointer-events-none" />
						<div
							className="absolute -top-8 -right-8 size-32 rounded-full blur-xl opacity-40 pointer-events-none"
							style={{ backgroundColor: headerGradient.glowColor }}
						/>
						<div
							className={`absolute -bottom-px left-0 right-0 h-5 [clip-path:ellipse(65%_100%_at_50%_100%)] ${
								themeTokens.isDark ? 'bg-slate-950' : 'bg-slate-50'
							}`}
						/>
					</div>

					{/* Floating Header Card */}
					<div className="px-4 -mt-16 relative z-10 text-center">
						<div
							className={`rounded-2xl p-3.5 shadow-md mx-auto inline-block w-full transition-colors duration-300 border ${themeTokens.cardBg}`}
						>
							<div
								className="size-14 rounded-xl mx-auto flex items-center justify-center text-lg font-black text-white shadow-xs mb-2"
								style={{ backgroundColor: accentColor }}
							>
								{content.title.slice(0, 2).toUpperCase()}
							</div>
							<h1
								className={`text-sm font-black tracking-tight leading-tight ${themeTokens.textPrimary}`}
							>
								{content.title}
							</h1>
							{content.subtitle && (
								<p className={`mt-1 text-[10px] font-medium ${themeTokens.textSecondary}`}>
									{content.subtitle}
								</p>
							)}
						</div>
					</div>

					{/* Social Links List */}
					<div className="px-3 mt-3 space-y-2">
						{content.links.map((item, index) => {
							const meta = PLATFORMS_BY_ID[item.platform] || {
								id: item.platform,
								name: item.platform,
								defaultLabel: item.platform,
								iconBg: 'bg-slate-900 text-white',
								renderIcon: (cls?: string) => <Share2 className={cls} />,
							};
							return (
								<ActionLink
									key={`${item.platform}-${index}`}
									href={item.url}
									type="open-social"
									targetPlatform={item.platform}
									targetLabel={item.label?.trim() || undefined}
									slug={experience.publicSlug}
									className={`flex items-center justify-between p-2.5 rounded-xl border shadow-2xs transition group ${themeTokens.actionBg}`}
								>
									<div className="flex items-center gap-2.5 truncate mr-2">
										<div
											className={`size-7 rounded-full flex items-center justify-center text-xs font-bold shadow-2xs shrink-0 ${meta.iconBg}`}
										>
											{meta.renderIcon('size-3.5')}
										</div>
										<span className={`text-xs font-semibold truncate ${themeTokens.textPrimary}`}>
											{item.label?.trim() || meta.name}
										</span>
									</div>
									<span className="text-slate-400 group-hover:text-slate-600 group-hover:translate-x-0.5 transition text-sm leading-none shrink-0">
										→
									</span>
								</ActionLink>
							);
						})}
					</div>
				</div>
			);
		}

		// Full-Scale Responsive Public Social Links Page (Desktop & Mobile)
		return (
			<main
				className={`w-full min-h-screen pb-16 transition-colors duration-300 relative ${themeTokens.pageBg}`}
			>
				<ThemeToggleWidget theme={activeTheme} onChange={setActiveTheme} />
				{/* Full-width Aurora Hero Banner */}
				<div
					className="w-full relative h-52 sm:h-64 flex flex-col items-center justify-start pt-8 text-white overflow-hidden"
					style={{
						backgroundColor: headerGradient.backgroundColor,
						backgroundImage: headerGradient.backgroundImage,
					}}
				>
					<div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff0d_1px,transparent_1px),linear-gradient(to_bottom,#ffffff0d_1px,transparent_1px)] bg-[size:16px_16px] pointer-events-none" />
					<div
						className="absolute -top-10 -right-10 size-64 rounded-full blur-2xl opacity-40 pointer-events-none"
						style={{ backgroundColor: headerGradient.glowColor }}
					/>
					<div
						className={`absolute -bottom-px left-0 right-0 h-6 [clip-path:ellipse(65%_100%_at_50%_100%)] ${
							themeTokens.isDark ? 'bg-slate-950' : 'bg-slate-100/70'
						}`}
					/>
				</div>

				{/* Floating Content Stack */}
				<div className="w-full max-w-xl md:max-w-2xl mx-auto px-4 -mt-24 sm:-mt-28 relative z-10 space-y-4">
					{/* Floating Header Card */}
					<div
						className={`rounded-2xl p-6 sm:p-7 shadow-xs text-center w-full transition-colors duration-300 border ${themeTokens.cardBg}`}
					>
						<div
							className="size-20 sm:size-24 rounded-2xl mx-auto flex items-center justify-center text-2xl sm:text-3xl font-black text-white shadow-2xs mb-3"
							style={{ backgroundColor: accentColor }}
						>
							{content.title.slice(0, 2).toUpperCase()}
						</div>
						<h1
							className={`text-xl sm:text-2xl font-black tracking-tight leading-tight ${themeTokens.textPrimary}`}
						>
							{content.title}
						</h1>
						{content.subtitle && (
							<p
								className={`mt-2 text-sm sm:text-base font-medium leading-relaxed ${themeTokens.textSecondary}`}
							>
								{content.subtitle}
							</p>
						)}
					</div>

					{/* Social Links List */}
					<div className="space-y-3">
						{content.links.map((item, index) => {
							const meta = PLATFORMS_BY_ID[item.platform] || {
								id: item.platform,
								name: item.platform,
								defaultLabel: item.platform,
								iconBg: 'bg-slate-900 text-white',
								renderIcon: (cls?: string) => <Share2 className={cls} />,
							};
							return (
								<ActionLink
									key={`${item.platform}-${index}`}
									href={item.url}
									type="open-social"
									targetPlatform={item.platform}
									targetLabel={item.label?.trim() || undefined}
									slug={experience.publicSlug}
									className={`flex items-center justify-between p-4 sm:p-5 rounded-2xl border shadow-xs hover:border-slate-300 hover:shadow-md transition-all group ${themeTokens.actionBg}`}
								>
									<div className="flex items-center gap-3.5 truncate mr-3">
										<div
											className={`size-11 sm:size-12 rounded-2xl flex items-center justify-center text-lg font-bold shadow-xs shrink-0 ${meta.iconBg}`}
										>
											{meta.renderIcon('size-5 sm:size-6')}
										</div>
										<div className="truncate">
											<span
												className={`text-sm sm:text-base font-bold block truncate ${themeTokens.textPrimary}`}
											>
												{item.label?.trim() || meta.name}
											</span>
											<span
												className={`text-xs font-medium truncate block ${themeTokens.textSecondary}`}
											>
												{meta.name}
											</span>
										</div>
									</div>
									<span className="text-slate-400 group-hover:text-slate-700 group-hover:translate-x-1 transition text-lg leading-none shrink-0 font-bold">
										→
									</span>
								</ActionLink>
							);
						})}
					</div>
					<PublicExperienceFooter isDark={themeTokens.isDark} />
				</div>
			</main>
		);
	}

	// 2. Restaurant / Food & Beverage Menu Experience
	if (content.type === 'menu') {
		const accentColor = content.accentColor || '#d97706';
		const headerColor = content.headerColor || accentColor;
		const headerGradient = computeHeaderGradientStyle(headerColor);
		const currency = content.currency || '$';

		if (previewMode) {
			return (
				<div
					className={`w-full relative pb-10 transition-colors duration-300 ${themeTokens.pageBg}`}
				>
					<ThemeToggleWidget theme={activeTheme} onChange={setActiveTheme} />
					{/* Header Banner */}
					<div
						className="relative h-28 flex flex-col items-center justify-start pt-5 text-white overflow-hidden"
						style={{
							backgroundColor: headerGradient.backgroundColor,
							backgroundImage: headerGradient.backgroundImage,
						}}
					>
						<div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff0d_1px,transparent_1px),linear-gradient(to_bottom,#ffffff0d_1px,transparent_1px)] bg-[size:14px_14px] pointer-events-none" />
						<div
							className={`absolute -bottom-px left-0 right-0 h-4 [clip-path:ellipse(65%_100%_at_50%_100%)] ${
								themeTokens.isDark ? 'bg-slate-950' : 'bg-slate-50'
							}`}
						/>
					</div>

					{/* Restaurant Info Card */}
					<div className="px-3.5 -mt-14 relative z-10 text-center">
						<div
							className={`rounded-2xl p-3 shadow-md mx-auto inline-block w-full transition-colors duration-300 border ${themeTokens.cardBg}`}
						>
							{content.logoUrl ? (
								<div className="size-12 rounded-xl mx-auto flex items-center justify-center shadow-xs mb-1.5 p-1 border border-slate-100 overflow-hidden bg-white">
									{/* eslint-disable-next-line @next/next/no-img-element */}
									<img
										src={content.logoUrl}
										alt={content.restaurantName}
										className="size-full object-cover rounded-lg"
									/>
								</div>
							) : (
								<div
									className="size-12 rounded-xl mx-auto flex items-center justify-center text-white shadow-xs mb-1.5"
									style={{ backgroundColor: accentColor }}
								>
									<Utensils className="size-5" />
								</div>
							)}
							<h1
								className={`text-sm font-black tracking-tight leading-tight ${themeTokens.textPrimary}`}
							>
								{content.restaurantName}
							</h1>
							{content.tagline && (
								<p className="mt-0.5 text-[10px] font-medium text-amber-800 bg-amber-50 rounded-full px-2 py-0.5 inline-block">
									{content.tagline}
								</p>
							)}

							{/* Mini Action Row */}
							<div className="mt-2.5 flex items-center justify-center gap-1.5 flex-wrap">
								{content.phone && (
									<ActionLink
										href={`tel:${content.phone}`}
										type="call"
										targetLabel="Call to Reserve"
										slug={experience.publicSlug}
										className="text-[9px] font-bold px-2 py-1 rounded-md bg-slate-100 text-slate-700 hover:bg-slate-200 flex items-center gap-1"
									>
										<Phone className="size-2.5" />
										Call
									</ActionLink>
								)}
								{content.whatsApp && (
									<ActionLink
										href={`https://wa.me/${content.whatsApp.replace(/[^0-9]/g, '')}`}
										type="open-whatsapp"
										targetLabel="Order via WhatsApp"
										slug={experience.publicSlug}
										className="text-[9px] font-bold px-2 py-1 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200 flex items-center gap-1"
									>
										Order
									</ActionLink>
								)}
								{content.website && (
									<ActionLink
										href={content.website}
										type="open-website"
										targetLabel="Website"
										slug={experience.publicSlug}
										className="text-[9px] font-bold px-2 py-1 rounded-md bg-slate-100 text-slate-700 hover:bg-slate-200 flex items-center gap-1"
									>
										<Globe className="size-2.5" />
										Web
									</ActionLink>
								)}
							</div>
						</div>
					</div>

					{/* Menu Categories */}
					<div className="px-3.5 mt-3 space-y-3">
						{content.categories?.map((category) => (
							<div key={category.id} className="space-y-1.5">
								<h3
									className={`text-[11px] font-black tracking-tight px-0.5 uppercase ${themeTokens.textPrimary}`}
								>
									{category.name}
								</h3>
								<div className="space-y-1.5">
									{category.items.map((item) => (
										<div
											key={item.id}
											className={`rounded-xl p-2.5 border shadow-2xs flex justify-between items-start gap-2 transition-colors duration-300 ${themeTokens.cardBg}`}
										>
											<div className="min-w-0 flex-1">
												<div className="flex items-center gap-1">
													<span
														className={`text-[11px] font-bold leading-tight ${themeTokens.textPrimary}`}
													>
														{item.name}
													</span>
												</div>
												{item.description && (
													<p
														className={`text-[9.5px] line-clamp-2 mt-0.5 leading-snug ${themeTokens.textSecondary}`}
													>
														{item.description}
													</p>
												)}
												{item.dietary && item.dietary.length > 0 && (
													<div className="flex flex-wrap gap-1 mt-1">
														{item.dietary.map((d) => (
															<span
																key={d}
																className="text-[8px] bg-slate-100 text-slate-600 px-1 py-0.2 rounded font-medium capitalize"
															>
																{d.replace('-', ' ')}
															</span>
														))}
													</div>
												)}
											</div>
											<span className="text-[11px] font-black text-amber-600 shrink-0">
												{currency}
												{item.price}
											</span>
										</div>
									))}
								</div>
							</div>
						))}
					</div>
				</div>
			);
		}

		// Full Landing Page for Menu
		return (
			<main
				className={`min-h-screen w-full overflow-x-clip pb-16 transition-colors duration-300 relative ${themeTokens.pageBg}`}
			>
				<ThemeToggleWidget theme={activeTheme} onChange={setActiveTheme} />
				{/* Aurora Hero Banner */}
				<div
					className="relative h-44 sm:h-52 w-full flex flex-col items-center justify-start pt-8 text-white overflow-hidden"
					style={{
						backgroundColor: headerGradient.backgroundColor,
						backgroundImage: headerGradient.backgroundImage,
					}}
				>
					<div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff0d_1px,transparent_1px),linear-gradient(to_bottom,#ffffff0d_1px,transparent_1px)] bg-[size:16px_16px] pointer-events-none" />
					<div
						className="absolute -top-12 -right-12 size-48 rounded-full blur-2xl opacity-40 pointer-events-none"
						style={{ backgroundColor: headerGradient.glowColor }}
					/>
					<div
						className={`absolute -bottom-px left-0 right-0 h-6 [clip-path:ellipse(65%_100%_at_50%_100%)] ${
							themeTokens.isDark ? 'bg-slate-950' : 'bg-slate-50'
						}`}
					/>
				</div>

				<div className="max-w-xl mx-auto -mt-20 px-4 space-y-6 relative z-10">
					{/* Floating Header Card */}
					<div
						className={`rounded-3xl p-6 shadow-sm text-center space-y-4 transition-colors duration-300 border ${themeTokens.cardBg}`}
					>
						{content.logoUrl ? (
							<div className="size-20 rounded-2xl mx-auto flex items-center justify-center shadow-xs p-1.5 border border-slate-100 overflow-hidden bg-white">
								{/* eslint-disable-next-line @next/next/no-img-element */}
								<img
									src={content.logoUrl}
									alt={content.restaurantName}
									className="size-full object-cover rounded-xl"
								/>
							</div>
						) : (
							<div
								className="size-16 rounded-2xl mx-auto flex items-center justify-center text-white shadow-sm"
								style={{ backgroundColor: accentColor }}
							>
								<Utensils className="size-7" />
							</div>
						)}

						<div>
							<h1
								className={`text-2xl sm:text-3xl font-black tracking-tight ${themeTokens.textPrimary}`}
							>
								{content.restaurantName}
							</h1>
							{content.tagline && (
								<p className="mt-1 text-sm font-semibold text-amber-700 bg-amber-50 rounded-full px-3 py-1 inline-block">
									{content.tagline}
								</p>
							)}
						</div>

						{content.description && (
							<p
								className={`text-xs sm:text-sm leading-relaxed max-w-md mx-auto ${themeTokens.textSecondary}`}
							>
								{content.description}
							</p>
						)}

						{/* Quick Action Buttons */}
						<div className="flex flex-wrap items-center justify-center gap-2 pt-2 border-t border-slate-100">
							{content.phone && (
								<ActionLink
									href={`tel:${content.phone}`}
									type="call"
									targetLabel="Call to Reserve"
									slug={experience.publicSlug}
									className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-slate-900 text-white font-bold text-xs sm:text-sm hover:bg-slate-800 transition shadow-xs"
								>
									<Phone className="size-4" />
									Call to Reserve
								</ActionLink>
							)}
							{content.whatsApp && (
								<ActionLink
									href={`https://wa.me/${content.whatsApp.replace(/[^0-9]/g, '')}`}
									type="open-whatsapp"
									targetLabel="Order via WhatsApp"
									slug={experience.publicSlug}
									className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-emerald-600 text-white font-bold text-xs sm:text-sm hover:bg-emerald-700 transition shadow-xs"
								>
									Order on WhatsApp
								</ActionLink>
							)}
							{content.website && (
								<ActionLink
									href={content.website}
									type="open-website"
									targetLabel="Official Website"
									slug={experience.publicSlug}
									className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-white border border-slate-200 text-slate-700 font-bold text-xs sm:text-sm hover:bg-slate-50 transition shadow-2xs"
								>
									<Globe className="size-4" />
									Website
								</ActionLink>
							)}
							{content.address && (
								<ActionLink
									href={
										content.mapUrl ||
										`https://maps.google.com/?q=${encodeURIComponent(content.address)}`
									}
									type="open-map"
									targetLabel="View Directions"
									slug={experience.publicSlug}
									className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-white border border-slate-200 text-slate-700 font-bold text-xs sm:text-sm hover:bg-slate-50 transition shadow-2xs"
								>
									<MapPin className="size-4 text-rose-500" />
									Directions
								</ActionLink>
							)}
						</div>
					</div>

					{/* Menu Categories */}
					<div className="space-y-6">
						{content.categories?.map((category) => (
							<div key={category.id} className="space-y-3">
								<div className="border-b border-slate-200 pb-1.5">
									<h2 className={`text-lg font-black tracking-tight ${themeTokens.textPrimary}`}>
										{category.name}
									</h2>
									{category.description && (
										<p className={`text-xs font-medium ${themeTokens.textSecondary}`}>
											{category.description}
										</p>
									)}
								</div>

								<div className="grid gap-3 sm:grid-cols-1">
									{category.items.map((item) => (
										<div
											key={item.id}
											className={`rounded-2xl p-4 sm:p-5 border shadow-xs transition space-y-2 ${themeTokens.cardBg}`}
										>
											<div className="flex justify-between items-start gap-4">
												<div>
													<h3 className={`text-base font-bold ${themeTokens.textPrimary}`}>
														{item.name}
													</h3>
													{item.description && (
														<p
															className={`text-xs sm:text-sm mt-1 leading-relaxed ${themeTokens.textSecondary}`}
														>
															{item.description}
														</p>
													)}
												</div>
												<span className="text-base font-black text-amber-700 bg-amber-50 border border-amber-200/60 px-2.5 py-1 rounded-xl shrink-0">
													{currency}
													{item.price}
												</span>
											</div>

											{item.dietary && item.dietary.length > 0 && (
												<div className="flex flex-wrap gap-1.5 pt-1">
													{item.dietary.map((tag) => (
														<span
															key={tag}
															className="text-[11px] font-semibold bg-slate-100 text-slate-700 px-2 py-0.5 rounded-md capitalize"
														>
															{tag.replace('-', ' ')}
														</span>
													))}
												</div>
											)}
										</div>
									))}
								</div>
							</div>
						))}
					</div>

					{/* Address Card */}
					{content.address && (
						<div className="bg-white rounded-2xl p-4 sm:p-5 border border-slate-200/90 shadow-xs flex items-center justify-between gap-4">
							<div className="flex items-center gap-3">
								<div className="size-10 rounded-xl bg-rose-50 text-rose-500 flex items-center justify-center shrink-0">
									<MapPin className="size-5" />
								</div>
								<div>
									<h4 className="text-xs font-bold text-slate-900">Location</h4>
									<p className="text-xs text-slate-600">{content.address}</p>
								</div>
							</div>
							<ActionLink
								href={
									content.mapUrl ||
									`https://maps.google.com/?q=${encodeURIComponent(content.address)}`
								}
								type="open-map"
								targetLabel="View Directions"
								slug={experience.publicSlug}
								className="text-xs font-bold text-blue-600 hover:underline shrink-0"
							>
								Open Map
							</ActionLink>
						</div>
					)}
					<PublicExperienceFooter isDark={themeTokens.isDark} />
				</div>
			</main>
		);
	}

	// 3. PDF Document / Brochure Experience
	if (content.type === 'pdf') {
		const accentColor = content.accentColor || '#1d4ed8';
		const headerColor = content.headerColor || accentColor;
		const headerGradient = computeHeaderGradientStyle(headerColor);

		const isUploadedPdf = Boolean(content.pdfUrl?.startsWith('data:application/pdf'));
		const isRealSlug = Boolean(
			experience.publicSlug && !experience.publicSlug.startsWith('preview'),
		);
		const pdfViewHref =
			isUploadedPdf && isRealSlug
				? `/api/experience/${experience.publicSlug}/pdf`
				: content.pdfUrl || '#';
		const pdfDownloadHref =
			isUploadedPdf && isRealSlug
				? `/api/experience/${experience.publicSlug}/pdf?download=1`
				: content.pdfUrl || '#';
		const pdfDownloadName = `${(content.title || 'document').trim().replace(/[^a-zA-Z0-9_\-]/g, '_') || 'document'}.pdf`;

		if (previewMode) {
			return (
				<div className="w-full bg-slate-50 relative pb-10">
					{/* Header Banner */}
					<div
						className="relative h-28 flex flex-col items-center justify-start pt-5 text-white overflow-hidden"
						style={{
							backgroundColor: headerGradient.backgroundColor,
							backgroundImage: headerGradient.backgroundImage,
						}}
					>
						<div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff0d_1px,transparent_1px),linear-gradient(to_bottom,#ffffff0d_1px,transparent_1px)] bg-[size:14px_14px] pointer-events-none" />
						<div className="absolute -bottom-px left-0 right-0 h-4 bg-slate-50 [clip-path:ellipse(65%_100%_at_50%_100%)]" />
					</div>

					{/* PDF Info Card */}
					<div className="px-3.5 -mt-14 relative z-10 text-center">
						<div className="bg-white rounded-2xl p-3.5 shadow-md border border-slate-100 mx-auto inline-block w-full">
							{content.coverUrl ? (
								<div className="size-14 rounded-xl mx-auto flex items-center justify-center shadow-xs mb-2 p-1 border border-slate-100 overflow-hidden bg-white">
									{/* eslint-disable-next-line @next/next/no-img-element */}
									<img
										src={content.coverUrl}
										alt={content.title}
										className="size-full object-cover rounded-lg"
									/>
								</div>
							) : (
								<div
									className="size-12 rounded-xl mx-auto flex items-center justify-center text-white shadow-xs mb-2"
									style={{ backgroundColor: accentColor }}
								>
									<FileText className="size-6" />
								</div>
							)}
							<h1 className="text-sm font-black tracking-tight text-slate-900 leading-tight">
								{content.title}
							</h1>
							{content.company && (
								<p className="mt-0.5 text-[10px] font-semibold text-slate-500">{content.company}</p>
							)}

							<div className="mt-2 flex items-center justify-center gap-1.5">
								<span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200/60">
									PDF
								</span>
								{content.fileSize && (
									<span className="text-[9px] font-medium text-slate-500">{content.fileSize}</span>
								)}
								{content.pageCount && (
									<span className="text-[9px] font-medium text-slate-500">
										• {content.pageCount}
									</span>
								)}
							</div>

							{/* Primary Action Button */}
							<div className="mt-3 space-y-1.5">
								{content.directPdf && (
									<div className="rounded-lg bg-emerald-50 border border-emerald-200/80 p-2 text-center text-emerald-800">
										<p className="text-[10px] font-bold">Direct Open Active</p>
										<p className="text-[9px] text-emerald-600 mt-0.5">
											Scanning opens the PDF directly without showing this page.
										</p>
									</div>
								)}
								<ActionLink
									href={pdfViewHref}
									type="view-pdf"
									targetLabel="View PDF Document"
									slug={experience.publicSlug}
									className="w-full flex items-center justify-center gap-1.5 py-2 px-3 rounded-xl text-white text-xs font-bold shadow-xs hover:opacity-95 transition"
									style={{ backgroundColor: accentColor }}
								>
									<ExternalLink className="size-3.5" />
									View Document
								</ActionLink>

								<ActionLink
									href={pdfDownloadHref}
									type="download-pdf"
									targetLabel="Download PDF"
									download={pdfDownloadName}
									slug={experience.publicSlug}
									className="w-full flex items-center justify-center gap-1.5 py-1.5 px-3 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 text-[11px] font-semibold transition"
								>
									<Download className="size-3" />
									Download File
								</ActionLink>
							</div>
						</div>
					</div>

					{/* Description Card */}
					{content.description && (
						<div className="px-3.5 mt-3">
							<div className="bg-white rounded-xl p-3 border border-slate-200/90 shadow-2xs">
								<h3 className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">
									About this document
								</h3>
								<p className="text-[11px] text-slate-600 leading-relaxed">{content.description}</p>
							</div>
						</div>
					)}
				</div>
			);
		}

		// Full Landing Page for PDF
		return (
			<main className="min-h-screen bg-slate-50 text-slate-900 w-full overflow-x-clip pb-16">
				{/* Aurora Hero Banner */}
				<div
					className="relative h-44 sm:h-52 w-full flex flex-col items-center justify-start pt-8 text-white overflow-hidden"
					style={{
						backgroundColor: headerGradient.backgroundColor,
						backgroundImage: headerGradient.backgroundImage,
					}}
				>
					<div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff0d_1px,transparent_1px),linear-gradient(to_bottom,#ffffff0d_1px,transparent_1px)] bg-[size:16px_16px] pointer-events-none" />
					<div
						className="absolute -top-12 -right-12 size-48 rounded-full blur-2xl opacity-40 pointer-events-none"
						style={{ backgroundColor: headerGradient.glowColor }}
					/>
					<div className="absolute -bottom-px left-0 right-0 h-6 bg-slate-50 [clip-path:ellipse(65%_100%_at_50%_100%)]" />
				</div>

				<div className="max-w-xl mx-auto -mt-20 px-4 space-y-6 relative z-10">
					{/* Floating Document Header Card */}
					<div className="bg-white rounded-3xl p-6 sm:p-8 shadow-sm border border-slate-200/90 text-center space-y-5">
						{content.coverUrl ? (
							<div className="size-24 rounded-2xl mx-auto flex items-center justify-center shadow-xs p-1.5 border border-slate-100 overflow-hidden bg-white">
								{/* eslint-disable-next-line @next/next/no-img-element */}
								<img
									src={content.coverUrl}
									alt={content.title}
									className="size-full object-cover rounded-xl"
								/>
							</div>
						) : (
							<div
								className="size-16 sm:size-20 rounded-2xl mx-auto flex items-center justify-center text-white shadow-sm"
								style={{ backgroundColor: accentColor }}
							>
								<FileText className="size-8 sm:size-10" />
							</div>
						)}

						<div>
							<h1 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">
								{content.title}
							</h1>
							{content.company && (
								<p className="mt-1 text-sm font-semibold text-slate-500">{content.company}</p>
							)}
						</div>

						{/* Document Meta Badges */}
						<div className="flex items-center justify-center gap-2 flex-wrap text-xs text-slate-600 font-medium">
							<span className="font-bold px-3 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200/60">
								PDF Document
							</span>
							{content.fileSize && (
								<span className="bg-slate-100 px-3 py-1 rounded-full">{content.fileSize}</span>
							)}
							{content.pageCount && (
								<span className="bg-slate-100 px-3 py-1 rounded-full">{content.pageCount}</span>
							)}
						</div>

						{/* Action Buttons */}
						<div className="pt-2 space-y-3 max-w-sm mx-auto">
							<ActionLink
								href={pdfViewHref}
								type="view-pdf"
								targetLabel="View PDF Document"
								slug={experience.publicSlug}
								className="w-full inline-flex items-center justify-center gap-2 py-3.5 px-6 rounded-2xl text-white font-bold text-sm sm:text-base shadow-sm hover:opacity-95 transition"
								style={{ backgroundColor: accentColor }}
							>
								<ExternalLink className="size-5" />
								Open & View PDF
							</ActionLink>

							<ActionLink
								href={pdfDownloadHref}
								type="download-pdf"
								targetLabel="Download PDF"
								download={pdfDownloadName}
								slug={experience.publicSlug}
								className="w-full inline-flex items-center justify-center gap-2 py-3 px-6 rounded-2xl bg-slate-100 hover:bg-slate-200 text-slate-800 font-bold text-sm transition"
							>
								<Download className="size-4" />
								Download File
							</ActionLink>
						</div>
					</div>

					{/* Description Card */}
					{content.description && (
						<div className="bg-white rounded-2xl p-5 sm:p-6 border border-slate-200/90 shadow-xs space-y-2">
							<h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider">
								Document Overview
							</h3>
							<p className="text-sm text-slate-700 leading-relaxed">{content.description}</p>
						</div>
					)}

					{/* Contact Details Card */}
					{(content.website || content.email || content.phone) && (
						<div className="bg-white rounded-2xl p-5 sm:p-6 border border-slate-200/90 shadow-xs space-y-3">
							<h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider">
								Contact Publisher
							</h3>
							<div className="space-y-2">
								{content.website && (
									<ActionLink
										href={content.website}
										type="open-website"
										targetLabel="Official Website"
										slug={experience.publicSlug}
										className="flex items-center justify-between p-3 rounded-xl bg-slate-50 hover:bg-slate-100 text-slate-800 transition"
									>
										<span className="text-xs sm:text-sm font-semibold flex items-center gap-2">
											<Globe className="size-4 text-slate-500" />
											Official Website
										</span>
										<span className="text-xs font-bold text-slate-400">Visit →</span>
									</ActionLink>
								)}
								{content.email && (
									<ActionLink
										href={`mailto:${content.email}`}
										type="email"
										targetLabel="Send Email"
										slug={experience.publicSlug}
										className="flex items-center justify-between p-3 rounded-xl bg-slate-50 hover:bg-slate-100 text-slate-800 transition"
									>
										<span className="text-xs sm:text-sm font-semibold flex items-center gap-2">
											<Mail className="size-4 text-slate-500" />
											{content.email}
										</span>
										<span className="text-xs font-bold text-slate-400">Email →</span>
									</ActionLink>
								)}
								{content.phone && (
									<ActionLink
										href={`tel:${content.phone}`}
										type="call"
										targetLabel="Call Publisher"
										slug={experience.publicSlug}
										className="flex items-center justify-between p-3 rounded-xl bg-slate-50 hover:bg-slate-100 text-slate-800 transition"
									>
										<span className="text-xs sm:text-sm font-semibold flex items-center gap-2">
											<Phone className="size-4 text-slate-500" />
											{content.phone}
										</span>
										<span className="text-xs font-bold text-slate-400">Call →</span>
									</ActionLink>
								)}
							</div>
						</div>
					)}
					<PublicExperienceFooter isDark={themeTokens.isDark} />
				</div>
			</main>
		);
	}

	// 4. Coupon & Promotional Voucher Experience
	if (content.type === 'coupon') {
		const accentColor = content.accentColor || '#059669';
		const headerColor = content.headerColor || accentColor;
		const headerGradient = computeHeaderGradientStyle(headerColor);

		const handleCopyCode = (e: React.MouseEvent) => {
			e.preventDefault();
			e.stopPropagation();
			void navigator.clipboard.writeText(content.couponCode);
			setCouponCopied(true);
			recordAction(experience.publicSlug, 'copy-coupon', undefined, 'Copied Promo Code');
			window.setTimeout(() => setCouponCopied(false), 2500);
		};

		if (previewMode) {
			return (
				<div className="w-full bg-slate-50 relative pb-10">
					{/* Header Banner */}
					<div
						className="relative h-28 flex flex-col items-center justify-start pt-5 text-white overflow-hidden"
						style={{
							backgroundColor: headerGradient.backgroundColor,
							backgroundImage: headerGradient.backgroundImage,
						}}
					>
						<div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff0d_1px,transparent_1px),linear-gradient(to_bottom,#ffffff0d_1px,transparent_1px)] bg-[size:14px_14px] pointer-events-none" />
						<div className="absolute -bottom-px left-0 right-0 h-4 bg-slate-50 [clip-path:ellipse(65%_100%_at_50%_100%)]" />
					</div>

					{/* Coupon Card */}
					<div className="px-3.5 -mt-14 relative z-10 text-center">
						<div className="bg-white rounded-2xl p-4 shadow-md border border-slate-100 mx-auto inline-block w-full">
							<span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block">
								{content.company}
							</span>

							{content.discount && (
								<span
									className="mt-1 inline-block text-[11px] font-black px-2.5 py-0.5 rounded-full text-white shadow-2xs"
									style={{ backgroundColor: accentColor }}
								>
									{content.discount}
								</span>
							)}

							<h1 className="mt-1 text-sm font-black tracking-tight text-slate-900 leading-tight">
								{content.title}
							</h1>

							{/* Voucher Code Box */}
							<div className="mt-3 p-2.5 rounded-xl border-2 border-dashed border-emerald-300 bg-emerald-50/70 text-center">
								<span className="text-[9px] font-semibold text-emerald-800 uppercase tracking-wider block">
									Promo Code
								</span>
								<div className="mt-1 flex items-center justify-center gap-2">
									<span className="text-base font-mono font-black text-emerald-950 tracking-wider">
										{content.couponCode}
									</span>
									<button
										type="button"
										onClick={handleCopyCode}
										className="p-1 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 transition cursor-pointer"
										title="Copy Code"
									>
										{couponCopied ? <Check className="size-3" /> : <Copy className="size-3" />}
									</button>
								</div>
								{couponCopied && (
									<span className="text-[9px] text-emerald-700 font-bold block mt-0.5 animate-pulse">
										Copied to clipboard!
									</span>
								)}
							</div>

							{content.validUntil && (
								<p className="mt-2 text-[10px] font-medium text-slate-500">{content.validUntil}</p>
							)}

							{/* Quick Links */}
							{content.website && (
								<div className="mt-3">
									<ActionLink
										href={content.website}
										type="open-website"
										targetLabel="Redeem Online"
										slug={experience.publicSlug}
										className="w-full flex items-center justify-center gap-1 py-2 px-3 rounded-xl text-white text-xs font-bold transition shadow-xs"
										style={{ backgroundColor: accentColor }}
									>
										Redeem Online →
									</ActionLink>
								</div>
							)}
						</div>
					</div>

					{/* Terms */}
					{content.terms && (
						<div className="px-3.5 mt-3">
							<div className="bg-white rounded-xl p-3 border border-slate-200/90 shadow-2xs">
								<h3 className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">
									Terms & Conditions
								</h3>
								<p className="text-[10px] text-slate-600 leading-relaxed">{content.terms}</p>
							</div>
						</div>
					)}
				</div>
			);
		}

		// Full Landing Page for Coupon
		return (
			<main className="min-h-screen bg-slate-50 text-slate-900 w-full overflow-x-clip pb-16">
				{/* Aurora Hero Banner */}
				<div
					className="relative h-44 sm:h-52 w-full flex flex-col items-center justify-start pt-8 text-white overflow-hidden"
					style={{
						backgroundColor: headerGradient.backgroundColor,
						backgroundImage: headerGradient.backgroundImage,
					}}
				>
					<div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff0d_1px,transparent_1px),linear-gradient(to_bottom,#ffffff0d_1px,transparent_1px)] bg-[size:16px_16px] pointer-events-none" />
					<div
						className="absolute -top-12 -right-12 size-48 rounded-full blur-2xl opacity-40 pointer-events-none"
						style={{ backgroundColor: headerGradient.glowColor }}
					/>
					<div className="absolute -bottom-px left-0 right-0 h-6 bg-slate-50 [clip-path:ellipse(65%_100%_at_50%_100%)]" />
				</div>

				<div className="max-w-xl mx-auto -mt-20 px-4 space-y-6 relative z-10">
					{/* Floating Coupon Voucher Card */}
					<div className="bg-white rounded-3xl p-6 sm:p-8 shadow-sm border border-slate-200/90 text-center space-y-5">
						<div className="space-y-1">
							<span className="text-xs sm:text-sm font-bold uppercase tracking-wider text-slate-500">
								{content.company}
							</span>
							<h1 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">
								{content.title}
							</h1>
						</div>

						{content.discount && (
							<span
								className="inline-block text-sm sm:text-base font-black px-4 py-1.5 rounded-full text-white shadow-xs"
								style={{ backgroundColor: accentColor }}
							>
								{content.discount}
							</span>
						)}

						{/* Dashed Voucher Box */}
						<div className="p-5 sm:p-6 rounded-2xl border-2 border-dashed border-emerald-400 bg-emerald-50/60 text-center space-y-2">
							<span className="text-xs font-bold text-emerald-800 uppercase tracking-wider">
								Exclusive Promo Code
							</span>
							<div className="flex items-center justify-center gap-3">
								<span className="text-2xl sm:text-3xl font-mono font-black text-emerald-950 tracking-widest">
									{content.couponCode}
								</span>
							</div>

							<div className="pt-2">
								<button
									type="button"
									onClick={handleCopyCode}
									className="inline-flex items-center justify-center bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs sm:text-sm rounded-xl px-5 py-2 cursor-pointer shadow-xs"
								>
									{couponCopied ? (
										<>
											<Check className="mr-1.5 size-4" />
											Code Copied!
										</>
									) : (
										<>
											<Copy className="mr-1.5 size-4" />
											Copy Code
										</>
									)}
								</button>
							</div>

							{content.validUntil && (
								<p className="text-xs text-slate-500 font-medium pt-1">{content.validUntil}</p>
							)}
						</div>

						{/* Action Buttons */}
						<div className="pt-2 space-y-2.5 max-w-sm mx-auto">
							{content.website && (
								<ActionLink
									href={content.website}
									type="open-website"
									targetLabel="Redeem Online"
									slug={experience.publicSlug}
									className="w-full inline-flex items-center justify-center gap-2 py-3.5 px-6 rounded-2xl text-white font-bold text-sm sm:text-base shadow-xs hover:opacity-95 transition"
									style={{ backgroundColor: accentColor }}
								>
									Redeem Online Now →
								</ActionLink>
							)}
							{content.phone && (
								<ActionLink
									href={`tel:${content.phone}`}
									type="call"
									targetLabel="Call Store"
									slug={experience.publicSlug}
									className="w-full inline-flex items-center justify-center gap-2 py-2.5 px-6 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 font-semibold text-xs sm:text-sm transition"
								>
									<Phone className="size-4" />
									Call Store ({content.phone})
								</ActionLink>
							)}
						</div>
					</div>

					{/* Terms & Conditions Card */}
					{content.terms && (
						<div className="bg-white rounded-2xl p-5 sm:p-6 border border-slate-200/90 shadow-xs space-y-2">
							<h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider">
								Terms & Conditions
							</h3>
							<p className="text-xs sm:text-sm text-slate-700 leading-relaxed">{content.terms}</p>
						</div>
					)}

					{/* Store Address Card */}
					{content.address && (
						<div className="bg-white rounded-2xl p-4 sm:p-5 border border-slate-200/90 shadow-xs flex items-center justify-between gap-4">
							<div className="flex items-center gap-3">
								<div className="size-10 rounded-xl bg-slate-100 text-slate-700 flex items-center justify-center shrink-0">
									<MapPin className="size-5 text-rose-500" />
								</div>
								<div>
									<h4 className="text-xs font-bold text-slate-900">In-Store Redemption</h4>
									<p className="text-xs text-slate-600">{content.address}</p>
								</div>
							</div>
							<ActionLink
								href={`https://maps.google.com/?q=${encodeURIComponent(content.address)}`}
								type="open-map"
								targetLabel="View Directions"
								slug={experience.publicSlug}
								className="text-xs font-bold text-blue-600 hover:underline shrink-0"
							>
								Directions →
							</ActionLink>
						</div>
					)}
					<PublicExperienceFooter isDark={themeTokens.isDark} />
				</div>
			</main>
		);
	}

	// 5. App Landing Page / Store Download Experience
	if (content.type === 'app') {
		const accentColor = content.accentColor || '#2563eb';
		const headerColor = content.headerColor || accentColor;
		const headerGradient = computeHeaderGradientStyle(headerColor);

		// Miniature Phone Preview (in-editor sticky widget)
		if (previewMode) {
			return (
				<div className="w-full bg-slate-50 relative pb-10">
					{/* Header Gradient */}
					<div
						className="relative h-24 flex flex-col items-center justify-start pt-4 text-white overflow-hidden"
						style={{
							backgroundColor: headerGradient.backgroundColor,
							backgroundImage: headerGradient.backgroundImage,
						}}
					>
						<div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff0d_1px,transparent_1px),linear-gradient(to_bottom,#ffffff0d_1px,transparent_1px)] bg-[size:14px_14px] pointer-events-none" />
					</div>

					<div className="px-4 -mt-10 relative z-10 text-center">
						<div className="mx-auto size-20 rounded-2xl bg-white border-2 border-white shadow-md overflow-hidden flex items-center justify-center">
							{content.logoUrl ? (
								// eslint-disable-next-line @next/next/no-img-element
								<img
									src={content.logoUrl}
									alt={content.appName}
									className="size-full object-cover"
								/>
							) : (
								<div
									className="size-full flex items-center justify-center text-white"
									style={{ backgroundColor: accentColor }}
								>
									<Smartphone className="size-10 text-white" />
								</div>
							)}
						</div>

						<h1 className="mt-2 text-sm font-black tracking-tight text-slate-900 leading-tight">
							{content.appName}
						</h1>
						{content.developer && (
							<p className="text-[10px] text-slate-500 font-semibold mt-0.5">{content.developer}</p>
						)}

						{/* Store Download Buttons */}
						<div className="mt-3 space-y-1.5">
							{content.iosUrl && (
								<ActionLink
									href={content.iosUrl}
									type="download-app-ios"
									targetLabel="App Store (iOS)"
									slug={experience.publicSlug}
									className="w-full flex items-center justify-center gap-2 py-2 px-3 rounded-xl bg-slate-900 text-white text-xs font-bold shadow-xs hover:bg-black transition"
								>
									<Download className="size-3.5" />
									Download on App Store
								</ActionLink>
							)}

							{content.androidUrl && (
								<ActionLink
									href={content.androidUrl}
									type="download-app-android"
									targetLabel="Google Play (Android)"
									slug={experience.publicSlug}
									className="w-full flex items-center justify-center gap-2 py-2 px-3 rounded-xl bg-slate-900 text-white text-xs font-bold shadow-xs hover:bg-black transition"
								>
									<Download className="size-3.5" />
									Get on Google Play
								</ActionLink>
							)}

							{content.websiteUrl && (
								<ActionLink
									href={content.websiteUrl}
									type="open-website"
									targetLabel="Official Website"
									slug={experience.publicSlug}
									className="w-full flex items-center justify-center gap-1.5 py-1.5 px-3 rounded-xl bg-white border border-slate-200 text-slate-700 text-[11px] font-semibold hover:bg-slate-50 transition"
								>
									<Globe className="size-3" />
									Visit Website
								</ActionLink>
							)}
						</div>

						{/* Description Card */}
						{content.description && (
							<div className="mt-3 text-left bg-white rounded-xl p-3 border border-slate-200/80 shadow-2xs">
								<h3 className="text-[11px] font-bold text-slate-900">About the App</h3>
								<p className="mt-1 text-[10px] text-slate-600 leading-relaxed whitespace-pre-line">
									{content.description}
								</p>
							</div>
						)}
					</div>
				</div>
			);
		}

		// Full Public Experience Page
		return (
			<main className="min-h-screen bg-slate-100 flex flex-col items-center justify-start pb-12 sm:pb-16 antialiased">
				{/* Header with Aurora Banner */}
				<div
					className="w-full h-44 sm:h-56 relative flex items-center justify-center text-white overflow-hidden shadow-xs"
					style={{
						backgroundColor: headerGradient.backgroundColor,
						backgroundImage: headerGradient.backgroundImage,
					}}
				>
					<div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff0d_1px,transparent_1px),linear-gradient(to_bottom,#ffffff0d_1px,transparent_1px)] bg-[size:16px_16px] pointer-events-none" />
					<div
						className="absolute -top-12 -right-12 size-48 rounded-full blur-2xl opacity-50 pointer-events-none"
						style={{ backgroundColor: headerGradient.glowColor }}
					/>
				</div>

				{/* Main Card Container */}
				<div className="w-full max-w-md sm:max-w-lg px-4 -mt-20 sm:-mt-24 relative z-10 space-y-4">
					<div className="bg-white rounded-3xl p-6 sm:p-8 shadow-sm border border-slate-200/90 text-center">
						<div className="mx-auto size-24 sm:size-28 rounded-3xl bg-white border-4 border-white shadow-lg overflow-hidden flex items-center justify-center -mt-16 sm:-mt-20 mb-4">
							{content.logoUrl ? (
								// eslint-disable-next-line @next/next/no-img-element
								<img
									src={content.logoUrl}
									alt={content.appName}
									className="size-full object-cover"
								/>
							) : (
								<div
									className="size-full flex items-center justify-center text-white"
									style={{ backgroundColor: accentColor }}
								>
									<Smartphone className="size-12 sm:size-14 text-white" />
								</div>
							)}
						</div>

						<h1 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">
							{content.appName}
						</h1>

						{content.developer && (
							<p className="mt-1 text-sm font-semibold text-slate-500">{content.developer}</p>
						)}

						{/* Action Buttons */}
						<div className="mt-6 space-y-3">
							{content.iosUrl && (
								<ActionLink
									href={content.iosUrl}
									type="download-app-ios"
									targetLabel="App Store (iOS)"
									slug={experience.publicSlug}
									className="w-full inline-flex items-center justify-center gap-3 py-3.5 px-6 rounded-2xl bg-slate-950 hover:bg-black text-white font-bold text-sm sm:text-base shadow-sm transition hover:scale-[1.01] active:scale-[0.99]"
								>
									<Download className="size-5" />
									<span>Download on the App Store</span>
								</ActionLink>
							)}

							{content.androidUrl && (
								<ActionLink
									href={content.androidUrl}
									type="download-app-android"
									targetLabel="Google Play (Android)"
									slug={experience.publicSlug}
									className="w-full inline-flex items-center justify-center gap-3 py-3.5 px-6 rounded-2xl bg-slate-950 hover:bg-black text-white font-bold text-sm sm:text-base shadow-sm transition hover:scale-[1.01] active:scale-[0.99]"
								>
									<Download className="size-5" />
									<span>Get it on Google Play</span>
								</ActionLink>
							)}

							{content.websiteUrl && (
								<ActionLink
									href={content.websiteUrl}
									type="open-website"
									targetLabel="Official Website"
									slug={experience.publicSlug}
									className="w-full inline-flex items-center justify-center gap-2 py-3 px-5 rounded-2xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold text-xs sm:text-sm transition"
								>
									<Globe className="size-4" />
									<span>Visit Official Website</span>
								</ActionLink>
							)}
						</div>

						{/* Description */}
						{content.description && (
							<div className="mt-6 pt-6 border-t border-slate-100 text-left">
								<h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider mb-2">
									About this app
								</h3>
								<p className="text-xs sm:text-sm text-slate-600 leading-relaxed whitespace-pre-line">
									{content.description}
								</p>
							</div>
						)}
					</div>
					<PublicExperienceFooter isDark={themeTokens.isDark} />
				</div>
			</main>
		);
	}

	// 6. High-Converting Business Page / Digital Business Card
	const accentColor = content.accentColor || '#1d4ed8';
	const headerColor = content.headerColor || accentColor;
	const headerGradient = computeHeaderGradientStyle(headerColor);
	const displayTitle = content.company || content.fullName;
	const displayTagline = content.tagline || content.role || 'Professional Services';
	const hoursList =
		content.openingHours && content.openingHours.length > 0 ? content.openingHours : DEFAULT_HOURS;
	const liveStatus = getLiveOpeningStatus(hoursList);
	const todayName = getTodayName();

	// Miniature Phone Preview (for in-editor sticky widget)
	if (previewMode) {
		return (
			<div className={`w-full relative pb-10 transition-colors duration-300 ${themeTokens.pageBg}`}>
				<ThemeToggleWidget theme={activeTheme} onChange={setActiveTheme} />
				{/* Dynamic Aurora Hero Banner */}
				<div
					className="relative h-32 flex flex-col items-center justify-start pt-6 text-white overflow-hidden"
					style={{
						backgroundColor: headerGradient.backgroundColor,
						backgroundImage: headerGradient.backgroundImage,
					}}
				>
					<div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff0d_1px,transparent_1px),linear-gradient(to_bottom,#ffffff0d_1px,transparent_1px)] bg-[size:14px_14px] pointer-events-none" />
					<div
						className="absolute -top-8 -right-8 size-32 rounded-full blur-xl opacity-40 pointer-events-none"
						style={{ backgroundColor: headerGradient.glowColor }}
					/>
					<div
						className={`absolute -bottom-px left-0 right-0 h-5 [clip-path:ellipse(65%_100%_at_50%_100%)] ${
							themeTokens.isDark ? 'bg-slate-950' : 'bg-slate-50'
						}`}
					/>
				</div>

				{/* Floating Elevated Logo Card */}
				<div className="px-4 -mt-16 relative z-10 text-center">
					<div
						className={`rounded-2xl p-3.5 shadow-md mx-auto inline-block w-full transition-colors duration-300 border ${themeTokens.cardBg}`}
					>
						{content.logoUrl ? (
							<div className="size-14 bg-white rounded-xl mx-auto flex items-center justify-center shadow-xs mb-2 p-1 border border-slate-100">
								{/* eslint-disable-next-line @next/next/no-img-element */}
								<img
									src={content.logoUrl}
									alt={displayTitle}
									className="max-h-full max-w-full object-contain rounded-lg"
								/>
							</div>
						) : (
							<div className="size-14 bg-slate-950 rounded-xl mx-auto flex flex-col items-center justify-center shadow-xs mb-2 text-white">
								<span className="text-xl font-black">{displayTitle.slice(0, 1).toUpperCase()}</span>
								<span className="text-[5px] uppercase tracking-tighter opacity-80">
									{displayTitle.slice(0, 12)}
								</span>
							</div>
						)}
						<h3
							className={`text-sm font-black tracking-tight leading-tight ${themeTokens.textPrimary}`}
						>
							{displayTitle}
						</h3>
						<div
							className={`inline-block mt-0.5 px-2 py-0.5 text-[10px] font-semibold rounded-full border ${themeTokens.badgeBg}`}
						>
							{displayTagline}
						</div>
					</div>
				</div>

				{/* Cards Stack inside Mini Phone */}
				<div className="px-3 mt-2.5 space-y-2">
					{/* Retractable Open Hours Accordion */}
					<div className="bg-white rounded-xl border border-slate-200/90 shadow-2xs overflow-hidden transition hover:border-slate-300">
						<button
							type="button"
							onClick={() => setHoursOpen((prev) => !prev)}
							className="w-full px-3 py-2.5 flex items-center justify-between text-left cursor-pointer select-none bg-white hover:bg-slate-50 transition"
						>
							<div className="flex items-center gap-1.5">
								<div className="p-1 rounded bg-blue-50 text-blue-600 text-[10px]">
									<Clock className="size-3" />
								</div>
								<div>
									<span className="text-[9px] font-medium text-slate-400 block leading-none">
										Schedule
									</span>
									<span className="text-[11px] font-bold text-slate-900 leading-tight">
										Open hours
									</span>
								</div>
							</div>
							<div className="flex items-center gap-1">
								<span
									className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold border ${
										liveStatus.isOpen
											? 'bg-emerald-50 text-emerald-700 border-emerald-200'
											: 'bg-rose-50 text-rose-700 border-rose-200'
									}`}
								>
									<span
										className={`size-1.5 rounded-full ${
											liveStatus.isOpen ? 'bg-emerald-500 animate-pulse' : 'bg-rose-500'
										}`}
									/>
									{liveStatus.isOpen ? 'Open now' : 'Closed'}
								</span>
								<ChevronDown
									className={`size-3 text-slate-400 transform transition-transform duration-200 ${
										hoursOpen ? 'rotate-180' : ''
									}`}
								/>
							</div>
						</button>

						{hoursOpen && (
							<div className="border-t border-slate-100 bg-slate-50/80 px-3 py-2 text-[10px] space-y-1">
								{hoursList.map((item) => {
									const isToday = item.day.toLowerCase() === todayName.toLowerCase();
									return (
										<div
											key={item.day}
											className={`flex justify-between items-center py-0.5 px-2 rounded ${
												isToday
													? 'font-semibold text-blue-900 bg-blue-100/60 border border-blue-200/50'
													: 'text-slate-600'
											}`}
										>
											<span className="flex items-center gap-1">
												{item.day}
												{isToday && (
													<span className="text-[8px] bg-blue-600 text-white px-1 py-0.2 rounded font-bold">
														TODAY
													</span>
												)}
											</span>
											<span>{item.isClosed ? 'Closed' : `${item.open} - ${item.close}`}</span>
										</div>
									);
								})}
							</div>
						)}
					</div>

					{/* Address Card */}
					{content.address && (
						<div className="bg-white rounded-xl border border-slate-200/90 p-3 shadow-2xs">
							<div className="flex items-center gap-2 pb-2 border-b border-slate-100">
								<div className="size-6 rounded-md bg-slate-100 flex items-center justify-center text-slate-600 text-xs">
									<MapPin className="size-3 text-rose-500" />
								</div>
								<span className="text-xs font-bold text-slate-900">Address</span>
							</div>
							<p className="mt-2 text-[11px] font-normal text-slate-600 leading-snug">
								{content.address}
							</p>
							<ActionLink
								href={
									content.mapUrl ??
									`https://maps.google.com/?q=${encodeURIComponent(content.address)}`
								}
								type="open-map"
								slug={experience.publicSlug}
								className="mt-2 inline-block text-blue-600 hover:text-blue-700 font-semibold text-[11px]"
							>
								Show on Map
							</ActionLink>
						</div>
					)}

					{/* Contact Person Card */}
					<div className="bg-white rounded-xl border border-slate-200/90 p-3 shadow-2xs space-y-2">
						<div className="flex items-center gap-1 text-[10px] font-semibold text-slate-500">
							<span className="text-blue-600">👤</span> Contact Person
						</div>
						<div>
							<div className="text-[11px] font-bold text-slate-900 leading-tight">
								{content.fullName}
							</div>
							{content.role && <div className="text-[10px] text-slate-500">{content.role}</div>}
						</div>

						{content.phone && (
							<div className="flex items-center justify-between bg-slate-50 p-1.5 rounded-lg border border-slate-200/80">
								<span className="text-[10px] font-semibold text-slate-800 truncate mr-1">
									{content.phone}
								</span>
								<div className="flex items-center gap-1 shrink-0">
									<button
										type="button"
										onClick={(e) => copyPhoneNumber(content.phone!, e)}
										className="px-1.5 py-0.5 text-slate-500 hover:text-slate-800 bg-white border border-slate-200 rounded text-[9px] cursor-pointer"
									>
										{phoneCopied ? 'Copied' : 'Copy'}
									</button>
									<a
										href={`tel:${content.phone}`}
										className="px-1.5 py-0.5 bg-blue-600 hover:bg-blue-700 text-white text-[9px] font-bold rounded"
									>
										Call
									</a>
								</div>
							</div>
						)}

						{content.email && (
							<div className="flex items-center justify-between bg-slate-50 p-1.5 rounded-lg border border-slate-200/80">
								<span className="text-[10px] font-semibold text-slate-800 truncate mr-1">
									{content.email}
								</span>
								<a
									href={`mailto:${content.email}`}
									className="px-1.5 py-0.5 bg-blue-600 hover:bg-blue-700 text-white text-[9px] font-bold rounded shrink-0"
								>
									Email
								</a>
							</div>
						)}

						{content.whatsApp && (
							<div className="flex items-center justify-between bg-slate-50 p-1.5 rounded-lg border border-slate-200/80">
								<span className="text-[10px] font-semibold text-slate-800 flex items-center gap-1">
									<span className="text-emerald-600 font-bold">💬</span> WhatsApp
								</span>
								<a
									href={`https://wa.me/${content.whatsApp.replace(/[^0-9]/g, '')}`}
									target="_blank"
									rel="noopener noreferrer"
									className="px-1.5 py-0.5 bg-emerald-600 hover:bg-emerald-700 text-white text-[9px] font-bold rounded flex items-center gap-0.5 shrink-0"
								>
									<span>Message</span> →
								</a>
							</div>
						)}

						{content.website && (
							<div className="flex items-center justify-between bg-slate-50 p-1.5 rounded-lg border border-slate-200/80">
								<span className="text-[10px] font-semibold text-slate-800 truncate mr-1">
									{content.website.replace(/^https?:\/\//, '')}
								</span>
								<ActionLink
									href={content.website}
									type="open-website"
									slug={experience.publicSlug}
									className="px-1.5 py-0.5 bg-blue-600 hover:bg-blue-700 text-white text-[9px] font-bold rounded shrink-0"
								>
									Visit →
								</ActionLink>
							</div>
						)}
					</div>

					{/* Our Social Networks */}
					{content.socialLinks && content.socialLinks.length > 0 && (
						<div>
							<h4 className="text-xs font-bold text-slate-900 mb-1.5 px-0.5">
								Our Social Networks
							</h4>
							<div className="space-y-1.5">
								{content.socialLinks.map((soc, idx) => {
									const meta = PLATFORMS_BY_ID[soc.platform] || {
										id: soc.platform,
										name: soc.platform,
										defaultLabel: soc.platform,
										iconBg: 'bg-slate-900 text-white',
										renderIcon: (cls?: string) => <Share2 className={cls} />,
									};
									return (
										<ActionLink
											key={`${soc.platform}-${idx}`}
											href={soc.url}
											type="open-social"
											targetPlatform={soc.platform}
											targetLabel={soc.label?.trim() || undefined}
											slug={experience.publicSlug}
											className="flex items-center justify-between p-2.5 bg-white rounded-xl border border-slate-200/90 shadow-2xs group cursor-pointer hover:border-slate-300 transition"
										>
											<div className="flex items-center gap-2.5 truncate mr-2">
												<div
													className={`size-7 rounded-full flex items-center justify-center text-xs font-bold shadow-2xs shrink-0 ${meta.iconBg}`}
												>
													{meta.renderIcon('size-3.5')}
												</div>
												<span className="text-xs font-semibold text-slate-900 truncate">
													{soc.label?.trim() || meta.name}
												</span>
											</div>
											<span className="text-slate-400 group-hover:text-slate-600 group-hover:translate-x-0.5 transition text-sm leading-none shrink-0">
												→
											</span>
										</ActionLink>
									);
								})}
							</div>
						</div>
					)}

					{/* About the Company */}
					{(content.about || content.bio) && (
						<div className="bg-white rounded-xl border border-slate-200/90 p-3 shadow-2xs">
							<h5 className="text-[9px] font-bold uppercase tracking-wider text-slate-400 mb-1">
								About the Company
							</h5>
							<p className="text-[10px] text-slate-600 leading-relaxed">
								{content.about || content.bio}
							</p>
						</div>
					)}

					{/* Facilities & Amenities */}
					{content.facilities && content.facilities.length > 0 && (
						<div className="bg-white rounded-xl border border-slate-200/90 p-2.5 shadow-2xs">
							<h5 className="text-[9px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">
								Facilities & Amenities
							</h5>
							<div className="grid grid-cols-4 gap-2 text-center text-[9px] font-semibold text-slate-600">
								{content.facilities.map((fid) => {
									const fac = FACILITIES_BY_ID[fid];
									if (!fac) return null;
									const Icon = fac.icon;
									return (
										<div key={fid} className="flex flex-col items-center gap-1">
											<div className="size-7 rounded-full bg-slate-100 flex items-center justify-center text-slate-700">
												<Icon className="size-3.5" />
											</div>
											<span className="truncate max-w-[60px]">{fac.label}</span>
										</div>
									);
								})}
							</div>
						</div>
					)}

					{/* Save & Exchange Contact Buttons */}
					<div className="flex gap-2">
						<button
							type="button"
							onClick={() => saveContact(experience)}
							style={{ backgroundColor: accentColor }}
							className="flex-1 py-2.5 text-white font-bold text-[11px] rounded-xl shadow-xs flex items-center justify-center gap-1.5 cursor-pointer hover:brightness-110 transition"
						>
							<Save className="size-3.5" />
							Save Contact
						</button>
						<button
							type="button"
							onClick={() => setShowExchangeModal(true)}
							className="flex-1 py-2.5 bg-slate-900 text-white font-bold text-[11px] rounded-xl shadow-xs flex items-center justify-center gap-1.5 cursor-pointer hover:bg-slate-800 transition"
						>
							<UserPlus className="size-3.5" />
							Exchange Info
						</button>
					</div>

					<ExchangeContactDialog
						isOpen={showExchangeModal}
						onClose={() => setShowExchangeModal(false)}
						slug={experience.publicSlug}
						ownerName={displayTitle || ''}
						accentColor={accentColor}
						onSaveContact={() => saveContact(experience)}
					/>
				</div>
			</div>
		);
	}

	// Full-Scale Responsive Public Digital Business Page (Desktop & Mobile)
	return (
		<main
			className={`w-full min-h-screen relative pb-16 transition-colors duration-300 ${themeTokens.pageBg}`}
		>
			<ThemeToggleWidget theme={activeTheme} onChange={setActiveTheme} />
			{/* Dynamic Aurora Hero Banner - Full Width Edge to Edge */}
			<div
				className="w-full relative h-52 sm:h-64 flex flex-col items-center justify-start pt-8 text-white overflow-hidden"
				style={{
					backgroundColor: headerGradient.backgroundColor,
					backgroundImage: headerGradient.backgroundImage,
				}}
			>
				<div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff0d_1px,transparent_1px),linear-gradient(to_bottom,#ffffff0d_1px,transparent_1px)] bg-[size:16px_16px] pointer-events-none" />
				<div
					className="absolute -top-10 -right-10 size-64 rounded-full blur-2xl opacity-40 pointer-events-none"
					style={{ backgroundColor: headerGradient.glowColor }}
				/>
				<div
					className={`absolute -bottom-px left-0 right-0 h-6 [clip-path:ellipse(65%_100%_at_50%_100%)] ${
						themeTokens.isDark ? 'bg-slate-950' : 'bg-slate-100/70'
					}`}
				/>
			</div>

			{/* Main Content Sections Stack (stretched wide with clean margin on mobile where background used to be) */}
			<div className="w-full max-w-xl md:max-w-2xl mx-auto px-4 -mt-24 sm:-mt-28 relative z-10 space-y-4">
				{/* Floating Elevated Logo / Header Card */}
				<div
					className={`rounded-2xl p-6 sm:p-7 shadow-xs border text-center w-full transition-colors duration-300 ${themeTokens.cardBg}`}
				>
					{content.logoUrl ? (
						<div
							className={`size-20 sm:size-24 rounded-2xl mx-auto flex items-center justify-center shadow-2xs mb-3 p-1.5 border ${
								themeTokens.isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-100'
							}`}
						>
							{/* eslint-disable-next-line @next/next/no-img-element */}
							<img
								src={content.logoUrl}
								alt={displayTitle}
								className="max-h-full max-w-full object-contain rounded-xl"
							/>
						</div>
					) : (
						<div className="size-20 sm:size-24 bg-slate-950 rounded-2xl mx-auto flex flex-col items-center justify-center shadow-2xs mb-3 text-white">
							<span className="text-3xl font-black">{displayTitle.slice(0, 1).toUpperCase()}</span>
							<span className="text-[8px] uppercase tracking-wider font-bold opacity-80">
								{displayTitle.slice(0, 12)}
							</span>
						</div>
					)}
					<h1
						className={`text-xl sm:text-2xl font-black tracking-tight leading-tight ${themeTokens.textPrimary}`}
					>
						{displayTitle}
					</h1>
					<div
						className={`inline-block mt-2 px-3.5 py-1 text-xs sm:text-sm font-semibold rounded-full border ${themeTokens.badgeBg}`}
					>
						{displayTagline}
					</div>
				</div>

				{/* Cards Stack */}
				<div className="space-y-4">
					{/* Retractable Open Hours Accordion */}
					<div
						className={`rounded-2xl border shadow-xs overflow-hidden transition ${themeTokens.cardBg}`}
					>
						<button
							type="button"
							onClick={() => setHoursOpen((prev) => !prev)}
							className="w-full px-5 py-4 flex items-center justify-between text-left cursor-pointer select-none transition hover:opacity-90"
						>
							<div className="flex items-center gap-3">
								<div className="p-2 rounded-xl bg-blue-50 text-blue-600">
									<Clock className="size-5" />
								</div>
								<div>
									<span
										className={`text-xs font-medium block leading-none ${themeTokens.textSecondary}`}
									>
										Schedule
									</span>
									<span
										className={`text-sm sm:text-base font-bold leading-tight ${themeTokens.textPrimary}`}
									>
										Opening Hours
									</span>
								</div>
							</div>
							<div className="flex items-center gap-2">
								<span
									className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border ${
										liveStatus.isOpen
											? 'bg-emerald-50 text-emerald-700 border-emerald-200'
											: 'bg-rose-50 text-rose-700 border-rose-200'
									}`}
								>
									<span
										className={`size-2 rounded-full ${
											liveStatus.isOpen ? 'bg-emerald-500 animate-pulse' : 'bg-rose-500'
										}`}
									/>
									{liveStatus.isOpen ? 'Open now' : 'Closed'}
								</span>
								<ChevronDown
									className={`size-4 text-slate-400 transform transition-transform duration-200 ${
										hoursOpen ? 'rotate-180' : ''
									}`}
								/>
							</div>
						</button>

						{hoursOpen && (
							<div
								className={`border-t px-5 py-3 text-xs sm:text-sm space-y-2 ${themeTokens.subCardBg}`}
							>
								{hoursList.map((item) => {
									const isToday = item.day.toLowerCase() === todayName.toLowerCase();
									return (
										<div
											key={item.day}
											className={`flex justify-between items-center py-1.5 px-3 rounded-xl ${
												isToday
													? 'font-bold text-blue-900 bg-blue-100/70 border border-blue-200'
													: `${themeTokens.textSecondary} font-medium`
											}`}
										>
											<span className="flex items-center gap-1.5">
												{item.day}
												{isToday && (
													<span className="text-[9px] bg-blue-600 text-white px-1.5 py-0.5 rounded-md font-bold">
														TODAY
													</span>
												)}
											</span>
											<span>{item.isClosed ? 'Closed' : `${item.open} - ${item.close}`}</span>
										</div>
									);
								})}
							</div>
						)}
					</div>

					{/* Address Card */}
					{content.address && (
						<div className={`rounded-2xl border p-5 shadow-xs ${themeTokens.cardBg}`}>
							<div className="flex items-center gap-2.5 pb-3 border-b border-slate-200/50">
								<div className="size-8 rounded-lg bg-rose-50 flex items-center justify-center text-rose-600">
									<MapPin className="size-4 text-rose-600" />
								</div>
								<span className={`text-sm sm:text-base font-bold ${themeTokens.textPrimary}`}>
									Location & Address
								</span>
							</div>
							<p
								className={`mt-3 text-sm sm:text-base font-normal leading-relaxed ${themeTokens.textSecondary}`}
							>
								{content.address}
							</p>
							<ActionLink
								href={
									content.mapUrl ??
									`https://maps.google.com/?q=${encodeURIComponent(content.address)}`
								}
								type="open-map"
								slug={experience.publicSlug}
								className="mt-3 inline-flex items-center gap-1.5 text-blue-600 hover:text-blue-700 font-bold text-sm"
							>
								<span>Open in Google Maps</span>
								<ExternalLink className="size-3.5" />
							</ActionLink>
						</div>
					)}

					{/* Contact Person Card */}
					<div className={`rounded-2xl border p-5 shadow-xs space-y-3.5 ${themeTokens.cardBg}`}>
						<div className="flex items-center justify-between pb-3 border-b border-slate-200/50">
							<div>
								<div
									className={`text-base sm:text-lg font-black leading-tight ${themeTokens.textPrimary}`}
								>
									{content.fullName}
								</div>
								{content.role && (
									<div
										className={`text-xs sm:text-sm font-semibold mt-0.5 ${themeTokens.textSecondary}`}
									>
										{content.role}
									</div>
								)}
							</div>
							<span className={`text-xs font-bold px-2.5 py-1 rounded-md ${themeTokens.badgeBg}`}>
								Contact
							</span>
						</div>

						{/* Phone */}
						{content.phone && (
							<div
								className={`flex items-center justify-between p-3 rounded-xl border ${themeTokens.subCardBg}`}
							>
								<span
									className={`text-sm sm:text-base font-semibold truncate mr-2 ${themeTokens.textPrimary}`}
								>
									{content.phone}
								</span>
								<div className="flex items-center gap-2 shrink-0">
									<button
										type="button"
										onClick={(e) => copyPhoneNumber(content.phone!, e)}
										className={`px-3 py-1.5 rounded-lg text-xs font-bold cursor-pointer transition shadow-2xs border ${
											themeTokens.isDark
												? 'bg-slate-800 text-slate-200 border-slate-700 hover:bg-slate-700'
												: 'bg-white text-slate-600 hover:text-slate-900 border-slate-200'
										}`}
									>
										{phoneCopied ? 'Copied' : 'Copy'}
									</button>
									<a
										href={`tel:${content.phone}`}
										onClick={() => recordAction(experience.publicSlug, 'call')}
										className="px-3.5 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-lg shadow-xs transition"
									>
										Call
									</a>
								</div>
							</div>
						)}

						{/* Email */}
						{content.email && (
							<div
								className={`flex items-center justify-between p-3 rounded-xl border ${themeTokens.subCardBg}`}
							>
								<span
									className={`text-sm sm:text-base font-semibold truncate mr-2 ${themeTokens.textPrimary}`}
								>
									{content.email}
								</span>
								<a
									href={`mailto:${content.email}`}
									onClick={() => recordAction(experience.publicSlug, 'email')}
									className="px-3.5 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-lg shadow-xs transition shrink-0"
								>
									Email
								</a>
							</div>
						)}

						{/* WhatsApp */}
						{content.whatsApp && (
							<div
								className={`flex items-center justify-between p-3 rounded-xl border ${themeTokens.subCardBg}`}
							>
								<span
									className={`text-sm sm:text-base font-semibold flex items-center gap-2 ${themeTokens.textPrimary}`}
								>
									<span className="text-emerald-600 font-bold text-lg">💬</span> WhatsApp
								</span>
								<a
									href={`https://wa.me/${content.whatsApp.replace(/[^0-9]/g, '')}`}
									target="_blank"
									rel="noopener noreferrer"
									onClick={() => recordAction(experience.publicSlug, 'open-whatsapp')}
									className="px-3.5 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-lg shadow-xs transition flex items-center gap-1 shrink-0"
								>
									<span>Message</span> →
								</a>
							</div>
						)}

						{/* Website Link */}
						{content.website && (
							<div
								className={`flex items-center justify-between p-3 rounded-xl border ${themeTokens.subCardBg}`}
							>
								<div className="truncate mr-2">
									<span
										className={`text-xs font-semibold block leading-none mb-0.5 ${themeTokens.textSecondary}`}
									>
										Website
									</span>
									<span
										className={`text-sm sm:text-base font-semibold truncate block ${themeTokens.textPrimary}`}
									>
										{content.website.replace(/^https?:\/\//, '')}
									</span>
								</div>
								<ActionLink
									href={content.website}
									type="open-website"
									slug={experience.publicSlug}
									className="px-3.5 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-lg shadow-xs transition shrink-0"
								>
									Visit Website →
								</ActionLink>
							</div>
						)}
					</div>

					{/* Social Channels List */}
					{content.socialLinks && content.socialLinks.length > 0 && (
						<div>
							<h4
								className={`text-sm sm:text-base font-bold mb-2.5 px-1 ${themeTokens.textPrimary}`}
							>
								Connect with Us
							</h4>
							<div className="space-y-2.5">
								{content.socialLinks.map((soc, idx) => {
									const meta = PLATFORMS_BY_ID[soc.platform] || {
										id: soc.platform,
										name: soc.platform,
										defaultLabel: soc.platform,
										iconBg: 'bg-slate-900 text-white',
										renderIcon: (cls?: string) => <Share2 className={cls} />,
									};
									return (
										<ActionLink
											key={`${soc.platform}-${idx}`}
											href={soc.url}
											type="open-social"
											targetPlatform={soc.platform}
											targetLabel={soc.label?.trim() || undefined}
											slug={experience.publicSlug}
											className={`flex items-center justify-between p-3.5 sm:p-4 rounded-2xl border shadow-xs hover:shadow-sm transition-all group cursor-pointer ${themeTokens.cardBg} hover:border-slate-400`}
										>
											<div className="flex items-center gap-3.5 truncate mr-2">
												<div
													className={`size-9 sm:size-10 rounded-full flex items-center justify-center text-sm font-bold shadow-xs shrink-0 ${meta.iconBg}`}
												>
													{meta.renderIcon('size-4 sm:size-5')}
												</div>
												<span
													className={`text-sm sm:text-base font-bold truncate ${themeTokens.textPrimary}`}
												>
													{soc.label?.trim() || meta.name}
												</span>
											</div>
											<span className="text-slate-400 group-hover:text-slate-700 group-hover:translate-x-1 transition text-lg leading-none shrink-0 font-bold">
												→
											</span>
										</ActionLink>
									);
								})}
							</div>
						</div>
					)}

					{/* About / Bio Card */}
					{(content.about || content.bio) && (
						<div className={`rounded-2xl border p-5 shadow-xs ${themeTokens.cardBg}`}>
							<h5 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">
								About Us
							</h5>
							<p
								className={`text-sm sm:text-base leading-relaxed whitespace-pre-line ${themeTokens.textSecondary}`}
							>
								{content.about || content.bio}
							</p>
						</div>
					)}

					{/* Facilities & Amenities */}
					{content.facilities && content.facilities.length > 0 && (
						<div className={`rounded-2xl border p-5 shadow-xs ${themeTokens.cardBg}`}>
							<h5 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-4">
								Facilities & Amenities
							</h5>
							<div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-3 text-center text-xs font-bold">
								{content.facilities.map((fid) => {
									const fac = FACILITIES_BY_ID[fid];
									if (!fac) return null;
									const Icon = fac.icon;
									return (
										<div
											key={fid}
											className={`flex flex-col items-center gap-2 p-3 rounded-xl border shadow-2xs ${themeTokens.subCardBg}`}
										>
											<div
												className={`size-10 sm:size-11 rounded-xl flex items-center justify-center shadow-2xs border ${
													themeTokens.isDark
														? 'bg-slate-800 border-slate-700 text-slate-200'
														: 'bg-white border-slate-200/80 text-slate-700'
												}`}
											>
												<Icon className="size-5" />
											</div>
											<span className={`leading-tight ${themeTokens.textPrimary}`}>
												{fac.label}
											</span>
										</div>
									);
								})}
							</div>
						</div>
					)}

					{/* Primary CTAs: Save Contact to Phone & Share Contact Back */}
					<div className="space-y-3">
						<button
							type="button"
							onClick={() => {
								recordAction(experience.publicSlug, 'save-contact');
								saveContact(experience);
							}}
							style={{ backgroundColor: accentColor }}
							className="w-full py-4 text-white font-black text-base sm:text-lg rounded-2xl shadow-lg flex items-center justify-center gap-2.5 cursor-pointer hover:brightness-110 active:scale-[0.99] transition-all"
						>
							<Save className="size-5" />
							Save Contact to Phone
						</button>

						<button
							type="button"
							onClick={() => setShowExchangeModal(true)}
							className="w-full py-3.5 bg-slate-900 dark:bg-white text-white dark:text-slate-900 font-bold text-base rounded-2xl shadow-md flex items-center justify-center gap-2.5 cursor-pointer hover:opacity-90 active:scale-[0.99] transition-all"
						>
							<UserPlus className="size-5" />
							🤝 Share Your Contact Back
						</button>
					</div>

					<ExchangeContactDialog
						isOpen={showExchangeModal}
						onClose={() => setShowExchangeModal(false)}
						slug={experience.publicSlug}
						ownerName={displayTitle || ''}
						accentColor={accentColor}
						onSaveContact={() => saveContact(experience)}
					/>
					<PublicExperienceFooter isDark={themeTokens.isDark} />
				</div>
			</div>
		</main>
	);
}
